import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchClassRoomParticipantsHistory } from '@core/actions/classRoom/participants';
import { participantsHistory } from '@core/selectors/classroom';
import { getCurrentSpaceId } from '@core/selectors/space';
import { AulaState } from '@core/types/state';

import ParticipantHistoryTitle from './ParticipantHistoryTitle';
import ParticipantHistoryDate from './ParticipantHistoryDate';
import ParticipantHistoryLoader from './ParticipantHistoryLoader';
import { ParticipantHistoryEvent } from './types';
import { useCallback } from 'react';
import { Box } from '@ublend-npm/aulaui-core';

const ParticipantHistory = () => {
  const spaceId = useSelector(getCurrentSpaceId);
  const items = useSelector((state: AulaState) =>
    participantsHistory(state, spaceId)
  ) as Record<string, Record<string, ParticipantHistoryEvent>>;
  const dispatch = useDispatch();

  const observerRef = useRef<IntersectionObserver | null>(null);
  const isFetching = useSelector(
    (state: AulaState) => state.classRoom.isFetchingParticipantsHistory
  );
  const lastEventDate = useSelector(
    (state: AulaState) => state.classRoom.lastEventDate
  );
  const hasFetchedAll = useSelector(
    (state: AulaState) => state.classRoom.hasFetchedAllParticipantsHistory
  );

  const loadingRef = useCallback(
    (node) => {
      if (isFetching) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          dispatch(
            fetchClassRoomParticipantsHistory({ before: lastEventDate })
          );
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetching]
  );

  useEffect(() => {
    dispatch(fetchClassRoomParticipantsHistory());
  }, [spaceId]);

  const titleRef = useRef<HTMLElement>(null);

  return (
    <>
      <ParticipantHistoryTitle ref={titleRef} />
      <Box
        sx={{
          overflow: 'auto',
          position: 'absolute',
          marginTop: `${
            titleRef.current
              ? titleRef.current.offsetTop + titleRef.current.offsetHeight - 24
              : 0
          }px`,
          inset: '24px',
        }}
      >
        {Object.keys(items).map((date) => (
          <ParticipantHistoryDate
            key={date}
            date={date}
            events={Object.values(items[date])}
          />
        ))}
        {hasFetchedAll ? null : <ParticipantHistoryLoader ref={loadingRef} />}
      </Box>
    </>
  );
};

export default ParticipantHistory;
